/* eslint jsx-a11y/no-static-element-interactions:0, jsx-a11y/click-events-have-key-events:0 */
import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import withStyles from '../withStyles';
import stylesGenerator from './styles';
import { lintText, addToDictionary } from './harperService';
import {
  normalizeLints,
  adjustLintsForEdit,
  buildSegments,
  isSpellingLint,
  applySuggestion,
  getSuggestionLabel,
  getKindLabel,
  getLintMessage,
  getIgnoreKey
} from './utils';

const LINT_DELAY_MS = 400;
const MAX_SUGGESTIONS = 5;

// Textarea styles copied onto the backdrop so its text wraps identically
const MIRRORED_STYLES = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'letterSpacing',
  'wordSpacing',
  'lineHeight',
  'textTransform',
  'textIndent',
  'tabSize',
  'direction',
  'unicodeBidi',
  'paddingTop',
  'paddingBottom',
  'paddingLeft',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderStyle'
];

/**
 * Textarea with Word-like spell & grammar checking powered by harper.js.
 *
 * A transparent textarea is layered over a "backdrop" mirroring its text, in which
 * flagged ranges are underlined (red = spelling, blue = grammar). Clicking (or right-clicking) a
 * flagged range opens a context menu with suggestions, "Ignore All" and "Add to Dictionary".
 *
 * Enable on a schema TextArea with `spellCheck: true`.
 */
class SpellCheckTextArea extends PureComponent {
  static propTypes = {
    inputProps: PropTypes.shape({
      value: PropTypes.string,
      onChange: PropTypes.func.isRequired,
      onKeyDown: PropTypes.func,
      disabled: PropTypes.bool,
      className: PropTypes.string,
      ref: PropTypes.func
    }).isRequired,
    // Called with the new text when a suggestion is applied
    onReplace: PropTypes.func.isRequired,
    computedStyles: PropTypes.object.isRequired // eslint-disable-line
  };

  state = {
    lints: [],
    ignored: [],
    menu: null // { lintIndex, x, y }
  };

  componentDidMount() {
    this.mounted = true;
    this.requestLint();
    document.addEventListener('mousedown', this.handleOutsideClick);
    window.addEventListener('resize', this.syncBackdrop);
    window.addEventListener('scroll', this.handlePageScroll, true);
    this.syncBackdrop();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.menu && this.state.menu !== prevState.menu) {
      this.keepMenuInViewport();
    }
    const prevValue = prevProps.inputProps.value || '';
    const value = this.getValue();
    if (prevValue !== value) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(({ lints }) => ({
        lints: adjustLintsForEdit(prevValue, value, lints),
        menu: null
      }));
      this.debouncedLint();
    }
    this.syncBackdrop();
  }

  componentWillUnmount() {
    this.mounted = false;
    this.debouncedLint.cancel();
    document.removeEventListener('mousedown', this.handleOutsideClick);
    window.removeEventListener('resize', this.syncBackdrop);
    window.removeEventListener('scroll', this.handlePageScroll, true);
  }

  handlePageScroll = e => {
    if (this.state.menu && !(this.menu && this.menu.contains(e.target))) {
      this.closeMenu();
    }
  };

  getValue = () => this.props.inputProps.value || '';

  getVisibleLints = () =>
    this.state.lints.filter(
      lint => !this.state.ignored.includes(getIgnoreKey(lint))
    );

  requestLint = () => {
    const text = this.getValue();
    this.lintRequestId = (this.lintRequestId || 0) + 1;
    const requestId = this.lintRequestId;

    if (!text.trim()) {
      this.setState({ lints: [], menu: null });
      return;
    }

    lintText(text)
      .then(lints => {
        // Drop stale responses if the text changed while linting
        if (this.mounted && requestId === this.lintRequestId) {
          this.setState({ lints: normalizeLints(text, lints), menu: null });
        }
      })
      .catch(() => {
        // Spell check is a non-blocking enhancement: on failure the textarea keeps working as usual
      });
  };

  debouncedLint = debounce(this.requestLint, LINT_DELAY_MS);

  // Keeps the backdrop aligned with the textarea (scroll, scrollbar, user resize)
  syncBackdrop = () => {
    const { textarea, backdrop } = this;
    if (!textarea || !backdrop) {
      return;
    }
    const style = window.getComputedStyle(textarea);
    MIRRORED_STYLES.forEach(prop => {
      backdrop.style[prop] = style[prop];
    });
    const scrollbarWidth =
      textarea.offsetWidth -
      textarea.clientWidth -
      parseFloat(style.borderLeftWidth) -
      parseFloat(style.borderRightWidth);

    backdrop.style.width = `${textarea.offsetWidth}px`;
    backdrop.style.height = `${textarea.offsetHeight}px`;
    backdrop.style.paddingRight = `${parseFloat(style.paddingRight) +
      Math.max(0, scrollbarWidth)}px`;
    backdrop.scrollTop = textarea.scrollTop;
    backdrop.scrollLeft = textarea.scrollLeft;
  };

  // Returns the underlined mark under the given viewport point, if any
  findMarkAtPoint = (x, y) => {
    if (!this.backdrop) {
      return null;
    }
    const marks = this.backdrop.querySelectorAll('[data-lint-index]');
    for (let i = 0; i < marks.length; i += 1) {
      const rects = marks[i].getClientRects();
      for (let j = 0; j < rects.length; j += 1) {
        const r = rects[j];
        if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
          return { el: marks[i], rect: r };
        }
      }
    }
    return null;
  };

  // Opens the suggestions popup just below the underlined word, like Word
  openMenuForMark = ({ el, rect }) =>
    this.setState({
      menu: {
        lintIndex: Number(el.getAttribute('data-lint-index')),
        x: rect.left,
        y: rect.bottom + 2
      }
    });

  handleContextMenu = e => {
    const mark = this.findMarkAtPoint(e.clientX, e.clientY);
    if (!mark) {
      this.closeMenu();
      return; // fall back to the browser's native context menu
    }
    e.preventDefault();
    this.openMenuForMark(mark);
  };

  // A plain click on an underlined word also shows its suggestions
  handleMouseUp = e => {
    this.syncBackdrop();
    if (e.button !== 0) {
      return;
    }
    const { selectionStart, selectionEnd } = this.textarea || {};
    const mark =
      selectionStart === selectionEnd &&
      this.findMarkAtPoint(e.clientX, e.clientY);
    if (mark) {
      this.openMenuForMark(mark);
    }
  };

  handleOutsideClick = e => {
    if (this.state.menu && this.menu && !this.menu.contains(e.target)) {
      this.closeMenu();
    }
  };

  handleKeyDown = e => {
    if (e.key === 'Escape' && this.state.menu) {
      // Keep Escape from also closing a surrounding popup
      e.preventDefault();
      this.closeMenu();
    }
    // Shift+F10 / Menu key: suggestions for the word at the caret
    if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
      const caret = this.textarea.selectionStart;
      const lintIndex = this.getVisibleLints().findIndex(
        lint => caret >= lint.start && caret <= lint.end
      );
      const el =
        lintIndex !== -1 &&
        this.backdrop.querySelector(`[data-lint-index="${lintIndex}"]`);
      if (el) {
        e.preventDefault();
        this.openMenuForMark({ el, rect: el.getClientRects()[0] });
      }
    }
    const { onKeyDown } = this.props.inputProps;
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  closeMenu = () => this.setState({ menu: null });

  keepMenuInViewport = () => {
    if (!this.menu) {
      return;
    }
    const rect = this.menu.getBoundingClientRect();
    if (rect.right > window.innerWidth) {
      this.menu.style.left = `${Math.max(
        0,
        window.innerWidth - rect.width - 4
      )}px`;
    }
    if (rect.bottom > window.innerHeight) {
      this.menu.style.top = `${Math.max(0, rect.top - rect.height - 24)}px`;
    }
  };

  handleSuggestion = (lint, suggestion) => {
    const { text, caret } = applySuggestion(this.getValue(), lint, suggestion);
    this.closeMenu();
    this.props.onReplace(text);
    if (this.textarea) {
      this.textarea.focus();
      // Wait for the new value to render before moving the caret
      setTimeout(() => {
        if (this.textarea) {
          this.textarea.setSelectionRange(caret, caret);
        }
      });
    }
  };

  handleIgnoreAll = lint =>
    this.setState(({ ignored }) => ({
      ignored: [...ignored, getIgnoreKey(lint)],
      menu: null
    }));

  handleAddToDictionary = lint => {
    this.closeMenu();
    addToDictionary(lint.problemText)
      .then(this.requestLint)
      .catch(() => {});
  };

  renderMenu(lints) {
    const { menu } = this.state;
    const { computedStyles } = this.props;
    const lint = menu && lints[menu.lintIndex];
    if (!lint) {
      return null;
    }
    const spelling = isSpellingLint(lint);
    const suggestions = lint.suggestions.slice(0, MAX_SUGGESTIONS);

    return ReactDOM.createPortal(
      <div
        ref={el => (this.menu = el)}
        className={computedStyles.menu}
        style={{ left: menu.x, top: menu.y }}
        role="menu"
        data-id="spellcheck-menu"
      >
        <div className={computedStyles.menuHeader}>
          <strong>{getKindLabel(lint)}</strong>
          <span>{getLintMessage(lint)}</span>
        </div>
        {suggestions.length ? (
          suggestions.map((suggestion, i) => (
            <button
              key={`${suggestion.kind}-${suggestion.text}-${i}`} // eslint-disable-line react/no-array-index-key
              type="button"
              role="menuitem"
              className={`${computedStyles.menuItem} ${
                computedStyles.suggestion
              }`}
              onClick={() => this.handleSuggestion(lint, suggestion)}
            >
              {getSuggestionLabel(lint, suggestion)}
            </button>
          ))
        ) : (
          <div className={computedStyles.menuEmpty}>No suggestions</div>
        )}
        <div className={computedStyles.menuDivider} />
        <button
          type="button"
          role="menuitem"
          className={computedStyles.menuItem}
          onClick={() => this.handleIgnoreAll(lint)}
        >
          Ignore All
        </button>
        {spelling ? (
          <button
            type="button"
            role="menuitem"
            className={computedStyles.menuItem}
            onClick={() => this.handleAddToDictionary(lint)}
          >
            Add to Dictionary
          </button>
        ) : null}
      </div>,
      document.body
    );
  }

  // Word-like proofing summary shown below the field
  renderStatus(lints) {
    const { computedStyles } = this.props;
    const spelling = lints.filter(isSpellingLint).length;
    const grammar = lints.length - spelling;
    if (!lints.length) {
      return null;
    }
    const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

    return (
      <div className={computedStyles.status} data-id="spellcheck-status">
        {spelling ? (
          <span className={computedStyles.statusSpelling}>
            {plural(spelling, 'spelling issue')}
          </span>
        ) : null}
        {grammar ? (
          <span className={computedStyles.statusGrammar}>
            {plural(grammar, 'grammar issue')}
          </span>
        ) : null}
        <span>Click an underlined word for suggestions</span>
      </div>
    );
  }

  render() {
    const { inputProps, computedStyles } = this.props;

    if (inputProps.disabled) {
      return <textarea {...inputProps} />;
    }

    const value = this.getValue();
    const lints = this.getVisibleLints();
    const segments = buildSegments(value, lints);

    return (
      <div className={computedStyles.container}>
        <div
          ref={el => (this.backdrop = el)}
          className={computedStyles.backdrop}
          aria-hidden="true"
        >
          {segments.map(
            (segment, i) =>
              segment.lintIndex === null ? (
                segment.text
              ) : (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  data-lint-index={segment.lintIndex}
                  className={
                    isSpellingLint(lints[segment.lintIndex])
                      ? computedStyles.spellingMark
                      : computedStyles.grammarMark
                  }
                >
                  {segment.text}
                </span>
              )
          )}
          {/* Trailing newlines are not rendered without content after them */}
          {'\n '}
        </div>
        <textarea
          {...inputProps}
          ref={el => {
            this.textarea = el;
            if (inputProps.ref) {
              inputProps.ref(el);
            }
          }}
          className={`${inputProps.className} ${computedStyles.textarea}`}
          spellCheck={false}
          onScroll={this.syncBackdrop}
          onMouseUp={this.handleMouseUp}
          onKeyDown={this.handleKeyDown}
          onContextMenu={this.handleContextMenu}
        />
        {this.renderStatus(lints)}
        {this.renderMenu(lints)}
      </div>
    );
  }
}

export default withStyles(stylesGenerator)(SpellCheckTextArea);
